<template>
    <ModalShell :title="title" :subtitle="subtitle" width="sm:max-w-[560px] sm:w-full w-11/12">
        <template #icon>
            <PhCreditCard :size="24" weight="regular" />
        </template>
        <template #header-action>
            <button type="button" class="close-btn" :disabled="isPaying" aria-label="Close" @click="close">
                <PhX :size="20" />
            </button>
        </template>

        <div class="pay-body">
            <!-- REVIEW. Every figure below arrives from the server, so what is on
                 screen is what the charge takes. -->
            <template v-if="phase !== 'done'">
                <PrimeSkeleton v-if="isLoadingPreview" height="96px" />

                <dl v-else class="pay-lines">
                    <template v-if="single">
                        <div class="pay-line">
                            <dt>Invoice</dt>
                            <dd>
                                {{ invoices[0].id }}
                                <span v-if="invoices[0].description" class="pay-line__sub">{{ invoices[0].description }}</span>
                            </dd>
                        </div>
                        <div class="pay-line">
                            <dt>Amount</dt>
                            <dd data-testid="pay-amount">{{ formatCurrency(amountCents) }}</dd>
                        </div>
                        <!-- Shown only when credit actually moves: a "$0.00 applied" row is
                             noise on the invoices that have no credit behind them. -->
                        <div v-if="creditAppliedCents > 0" class="pay-line">
                            <dt>Account credit applied</dt>
                            <dd data-testid="pay-credit">−{{ formatCurrency(creditAppliedCents) }}</dd>
                        </div>
                        <div v-if="preview" class="pay-line pay-line--total">
                            <dt>Charged to your card</dt>
                            <dd data-testid="pay-due">{{ formatCurrency(preview.amount_due_cents) }}</dd>
                        </div>
                    </template>
                    <template v-else>
                        <div class="pay-line">
                            <dt>Open invoices</dt>
                            <dd data-testid="pay-count">{{ invoices.length }}</dd>
                        </div>
                        <div class="pay-line pay-line--total">
                            <dt>Total invoiced</dt>
                            <dd data-testid="pay-amount">{{ formatCurrency(amountCents) }}</dd>
                        </div>
                    </template>

                    <div class="pay-line">
                        <dt>Card</dt>
                        <dd>
                            <span v-if="cardBrand" class="pay-card" data-testid="pay-card">
                                <CardBrandIcon :brand="cardBrand" />
                                <span v-if="cardLastFour">{{ maskedLastFour(cardLastFour) }}</span>
                            </span>
                            <span v-else-if="cardUnknown" data-testid="pay-card-unknown">Unavailable</span>
                            <!-- A card the summary could not describe is still a card. -->
                            <span v-else-if="hasCard" data-testid="pay-card-unnamed">Card on file</span>
                            <span v-else data-testid="pay-no-card">No card on file</span>
                            <!-- WHENEVER NOTHING PINS THE CARD, which is not the same set as
                                 "a batch": `approvedFor` promises figures only where a preview
                                 confirmed them, and a single RENEWAL has no preview either —
                                 the API refuses to quote one. So the qualifier keys on the
                                 preview, not on `single`: without one the charge takes whichever
                                 card is primary as it runs, which may not be the summary's. -->
                            <span v-if="!preview && hasCard" class="pay-line__sub" data-testid="pay-card-unpinned">
                                {{ unpinnedCardNote }}
                            </span>
                        </dd>
                    </div>
                </dl>

                <!-- The preview refused, so there is no figure to promise. Say which
                     answer is missing rather than quoting the row's own total as though
                     the server had confirmed it. -->
                <p v-if="previewRefusal" class="pay-note" data-testid="pay-no-quote">
                    {{ previewRefusal }} The invoice's own amount is above; what the card takes
                    is settled as the charge runs.
                </p>

                <p v-if="!single" class="pay-note" data-testid="pay-all-note">
                    Each invoice is charged on its own, so one refusal leaves the rest paid.
                    Account credit is applied to each invoice as it is charged, so your card may
                    take less than the total above. Each charge goes to whichever card is primary
                    at that moment — pay an invoice on its own to hold the charge to the card and
                    the figure shown.
                </p>

                <p v-if="!previewFailure && !notPayable && !hasCard && chargeCents > 0" class="pay-warning" data-testid="pay-needs-card">
                    Add a card to this company's billing before paying.
                </p>

                <!-- The batch quotes nothing, so a renewal in it can be charged for more
                     than the total above. Named rather than hidden, and the single-invoice
                     press is the way to see a figure first. -->
                <p v-if="recalculatedCount > 0" class="pay-warning" data-testid="pay-recalculated">
                    {{ recalculatedCount === 1 ? 'One of these is a renewal' : `${recalculatedCount} of these are renewals` }},
                    and a renewal's total is recalculated as it is charged — so your card can take
                    more than the total above. Pay a renewal on its own to see its figure first.
                </p>

                <p v-if="staleNotice" class="pay-warning" data-testid="pay-stale">{{ staleNotice }}</p>

                <!-- We do not KNOW what paying would take. A different thing from "there
                     is no figure to promise", which is the refusal above: this is a
                     timeout, a lost session, a 5xx. Paying through it would take an
                     amount nobody reviewed, so there is no press until it is read. -->
                <p v-if="previewFailure" class="pay-warning" data-testid="pay-preview-failed">
                    {{ previewFailure }} Nothing has been charged.
                </p>

                <!-- The invoice moved while the list sat on screen — paid somewhere else,
                     or already resolving a charge in flight. There is no press that can
                     work, so the API's own sentence is shown and the press stays
                     disabled; closing refreshes the row that offered it. -->
                <p v-if="notPayable" class="pay-warning" data-testid="pay-not-payable">
                    {{ notPayable }} Nothing has been charged.
                </p>
            </template>

            <!-- DONE. Partial success is expressible and is reported as itself: two
                 paid, one still open. -->
            <template v-else>
                <p class="pay-outcome" data-testid="pay-outcome">{{ outcome }}</p>
                <ul v-if="failures.length" class="pay-failures">
                    <li v-for="failure in failures" :key="failure.id">
                        <strong>{{ failure.id }}</strong>
                        <span>{{ failure.message }}</span>
                    </li>
                </ul>
            </template>
        </div>

        <template #footer>
            <template v-if="phase === 'done'">
                <PrimeButton label="Done" data-testid="pay-done" @click="close" />
            </template>
            <template v-else>
                <PrimeButton class="p-btn-root_secondary" label="Cancel" :disabled="isPaying" @click="close" />
                <PrimeButton
                    v-if="previewFailure"
                    label="Try again"
                    :loading="isLoadingPreview"
                    data-testid="pay-retry"
                    @click="loadPreview"
                />
                <PrimeButton
                    v-else
                    :label="payLabel"
                    :loading="isPaying"
                    :disabled="isLoadingPreview || !canCharge"
                    data-testid="pay-confirm"
                    @click="pay"
                />
            </template>
        </template>
    </ModalShell>
</template>

<script setup lang="ts">
/**
 * PB-064: paying sits next to the invoice it pays.
 *
 * One invoice or several, and either way ONE CHARGE PER INVOICE — invoice and
 * payment stay 1:1, which is what the refund, dispute and reconciliation paths
 * assume. A refusal partway does not stop the list: the rest are still charged
 * and the outcome says which settled.
 *
 * For a single invoice the figures come from the server's pay preview
 * (PB-065-API) and are echoed back on the charge, so a credit balance that moved
 * in between is refused rather than silently taken. An invoice the preview will
 * not quote — a renewal, whose total the charge recalculates — is paid without
 * pinned figures, and the copy says so instead of quoting a number nothing holds.
 *
 * A card whose bank must authenticate a charge gets that bank step here, one invoice
 * at a time, and the invoice counts as paid only once app-api records the settled
 * payment (see `completeBankStep`).
 */
import { PhCreditCard, PhX } from '@phosphor-icons/vue';
import PrimeButton from 'primevue/button';
import PrimeSkeleton from 'primevue/skeleton';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatCurrency } from '@privacy-bee/format/currency';
import { maskedLastFour } from '@privacy-bee/format/mask';
import CardBrandIcon from '~/components/billing/CardBrandIcon.vue';
import ModalShell from '~/components/modals/shared/ModalShell.vue';
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useThreeDSecure } from '~/composables/useThreeDSecure';
import { businessService } from '~/services/business-service';
import type { ApprovedCharge, InvoicePayPreview, PayableInvoice } from '~/types/business';
import { ModalNames } from '~/types/modals';
import { paymentRefusalGroup } from 'pb-billing-ui/attempt/payment-refusals.ts';
import { PROCESSING_MESSAGE, type ThreeDSecureChallenge } from 'pb-billing-ui/attempt/three-d-secure.ts';
import { ApiError, memberFacingMessage } from '~/utils/errors';

const dialogRef = getDialogRef(ModalNames.PAY_INVOICES);
const authenticate = useThreeDSecure();

const data = computed(() => dialogRef.value?.data);
const companyId = computed(() => data.value?.companyId);
const invoices = computed<PayableInvoice[]>(() => data.value?.invoices ?? []);
const single = computed(() => invoices.value.length === 1);

const preview = ref<InvoicePayPreview | null>(null);
const previewRefusal = ref<string | null>(null);
/** The preview could not be READ — distinct from the API refusing to quote. See loadPreview. */
const previewFailure = ref<string | null>(null);
/** The API refused because this invoice is not payable at all — not merely unquotable. See loadPreview. */
const notPayable = ref<string | null>(null);
const isLoadingPreview = ref(false);
const staleNotice = ref<string | null>(null);
const phase = ref<'review' | 'paying' | 'done'>('review');
const isPaying = computed(() => phase.value === 'paying');

/**
 * Where one charge left its invoice. Five answers rather than paid-or-not, because each
 * tells the payer something different about their money:
 *
 *  - `stillOpen`: refused before anything was charged, so the invoice is still owed and
 *    paying it again cannot charge twice.
 *  - `onHold`: a bank step that took nothing — refused, closed, or never started. Nothing
 *    was charged, but app-api holds the invoice PENDING with that attempt recorded, so it
 *    cannot be paid from here until the attempt clears.
 *  - `movedOn`: paid elsewhere, voided, closed or gone before the charge, or failed by it.
 *    Nothing was charged and there is nothing left to pay from here.
 *  - `unconfirmed`: the charge may still take money — in flight, its answer lost, or
 *    settled against something it does not match — so the payer checks before paying
 *    again rather than being told the invoice is open.
 */
type PayBucket = 'paid' | 'stillOpen' | 'onHold' | 'movedOn' | 'unconfirmed';
type UnpaidBucket = Exclude<PayBucket, 'paid'>;
type PayResult = { id: string; bucket: PayBucket; message?: string };
const results = ref<PayResult[]>([]);
const failures = computed(() => results.value.filter((r) => r.bucket !== 'paid'));

/**
 * What the invoices are for. The preview's own `amount_cents` when there is one,
 * because that is the figure the charge read; otherwise the rows' own totals,
 * which is the same column the list rendered.
 */
const amountCents = computed(() =>
    preview.value?.amount_cents ?? invoices.value.reduce((sum, invoice) => sum + (invoice.amount || 0), 0),
);
const creditAppliedCents = computed(() => preview.value?.credit_applied_cents ?? 0);
/** What the card is expected to take, which is the amount the no-card refusal is about. */
const chargeCents = computed(() => preview.value?.amount_due_cents ?? amountCents.value);

// PRESENT, NOT NON-NULL. A preview that exists is authoritative about the card
// including when it says there is none: `card_brand` is `string | null` and never
// absent, so a null in a preview is the server reporting no card on file — removed
// in another session, most often — and not a gap to fill. Read with `??`, that null
// would fall through to the opener's summary: the modal would draw the removed card and
// enable a press the charge refuses, since `approvedFor` promises null. The API reads the
// echoed card the same way: absent means no promise, present-and-null means a promise of
// nothing, and presence is the only thing that tells them apart.
//
// The opener's summary is the fallback ONLY where there is no preview at all — the
// multi-invoice confirmation, and the renewal the API will not quote.
const cardBrand = computed(() => (preview.value ? preview.value.card_brand : data.value?.cardBrand ?? null));
const cardLastFour = computed(() => (preview.value ? preview.value.card_last_four : data.value?.cardLastFour ?? null));

// WHETHER there is a card, which is not the same question as what it looks like. A
// preview answers both — `card_brand` is null only when the server sees no card — but
// without one the brand comes from the opener's billing summary, whose `card_brand` is
// nullable metadata: a company with a card Stripe has not described leaves it empty.
// Reading presence off the brand there would disable the press and say "No card on file"
// on a company the page offers Pay all to, because the page gates that press on
// `has_payment_method` and not on the brand. So the flag is passed in and asked here,
// and the brand is left to do the only job it can do, which is describe.
//
// UNKNOWN IS NOT NONE. With no preview and an opener whose summary did not load, nothing
// says whether there is a card, so the row says that and the press is not withheld: a
// company with none is refused by the pay route as `no_payment_method`, which charges
// nothing and lands as still open.
const cardUnknown = computed(() => (
    !preview.value && data.value?.hasPaymentMethod === undefined && !data.value?.cardBrand
));
const hasCard = computed(() => (
    preview.value
        ? preview.value.card_brand !== null
        : cardUnknown.value || Boolean(data.value?.hasPaymentMethod ?? data.value?.cardBrand)
));

/** Said of one charge or of several, because the row above carries one card either way. */
const unpinnedCardNote = computed(() => (
    single.value ? 'primary card as the charge runs' : 'primary card as each charge runs'
));

/**
 * The invoices in a BATCH whose charge is settled as it runs, not before it.
 *
 * A renewal's total is recalculated by `SyncInvoiceWithAutoRenewAction` inside the
 * charge — lines drop for licenses that turned auto-renew off, and a newly eligible
 * license on the same date is added — which is exactly why the pay preview refuses to
 * quote one. A batch asks for no preview at all, so nothing pins any of its figures,
 * and "Total invoiced" above reads as the ceiling on what the card will take while a
 * renewal in the list can come back higher.
 *
 * Previewing each row instead would be one request per invoice on open — a hundred of
 * them on the page's own ceiling — and would still pin nothing for the renewals, since
 * the preview's answer for them is a refusal. So the figure does not claim to be a
 * bound: the renewals are counted and named, and paying one on its own is the way to
 * see what it takes first.
 */
const recalculatedCount = computed(() => (
    single.value ? 0 : invoices.value.filter((invoice) => invoice.type === 'renewal').length
));

const title = computed(() => (single.value ? 'Review and pay' : `Pay ${invoices.value.length} open invoices`));
const subtitle = computed(() =>
    single.value
        ? 'What paying this invoice takes, read from your account.'
        : 'Each invoice is charged separately, so you can settle some and leave the rest.',
);
// Only a figure the server CONFIRMED may be quoted on the press. Without a preview
// `chargeCents` is the invoice row's own total, and putting that on the button says
// the payer authorized it — while the copy three lines up says the renewal is
// recalculated as it is charged and may take something else. People read buttons, so
// the button names the act instead of a number nothing holds.
const payLabel = computed(() => {
    if (!single.value) return `Pay ${invoices.value.length} invoices`;

    return preview.value ? `Pay ${formatCurrency(chargeCents.value)}` : 'Pay this invoice';
});

// A credit-covered invoice charges $0.00 and that press is correct, so the gate is
// the card — and only when the card is the thing that has to work. A preview that
// could not be READ is a separate refusal and an unconditional one: there is no
// answer to approve, so there is nothing to press whatever the figures look like.
const canCharge = computed(() =>
    invoices.value.length > 0
    && previewFailure.value === null
    && notPayable.value === null
    && (chargeCents.value === 0 || hasCard.value),
);

/** How many charges landed in each bucket, which is what the outcome line reports. */
const tally = computed(() => {
    const counts: Record<PayBucket, number> = { paid: 0, stillOpen: 0, onHold: 0, movedOn: 0, unconfirmed: 0 };
    for (const result of results.value) counts[result.bucket] += 1;

    return counts;
});

const outcome = computed(() => {
    const { paid, stillOpen: open, onHold: held, movedOn: moved, unconfirmed: unknown } = tally.value;

    const parts: string[] = [];
    if (paid > 0) parts.push(`${paid} paid`);
    if (open > 0) parts.push(`${open} still open`);
    if (held > 0) parts.push(`${held} on hold`);
    if (moved > 0) parts.push(`${moved} no longer payable`);
    if (unknown > 0) parts.push(`${unknown} not confirmed`);

    if (parts.length === 0) return 'Nothing was charged.';
    if (parts.length === 1 && paid > 0) return `${paid} ${paid === 1 ? 'invoice' : 'invoices'} paid.`;
    if (parts.length === 1 && open > 0) return `${open} ${open === 1 ? 'invoice is' : 'invoices are'} still open.`;
    if (parts.length === 1 && held > 0) return `${held} ${held === 1 ? 'invoice is' : 'invoices are'} on hold.`;
    if (parts.length === 1 && moved > 0) return `${moved} ${moved === 1 ? 'invoice is' : 'invoices are'} no longer payable.`;
    if (parts.length === 1) return `${unknown} ${unknown === 1 ? 'payment was' : 'payments were'} not confirmed.`;

    return `${parts.join(', ')}.`;
});

/**
 * The one pay-preview refusal that leaves an invoice payable without a quote: the
 * renewal whose total `Invoice::charge()` recalculates as it runs.
 *
 * A reason and not a status, because the status is shared — see `loadPreview`. A 422
 * that names any other reason, or none, closes the press. That is the safe half of the
 * split: an invoice the server has not called the renewal case stays unpayable from
 * here, rather than an invoice that has already been paid being offered a press the
 * charge must turn down.
 */
const UNQUOTABLE_RENEWAL = 'renewal_not_quotable';

/** The modal's own words for an invoice it cannot pay, wherever the API has not said it in its own. */
const NOT_PAYABLE_SENTENCE = 'This invoice can no longer be paid from here.';

/**
 * Read the quote for a single invoice.
 *
 * TWO FAILURES, and only one of them may let a charge through. A 422 is the API
 * saying there is no figure to promise — a renewal, whose total the charge
 * recalculates, or anything the charge itself would refuse — and its own sentence
 * says which, so the invoice is paid promising nothing and pinning nothing.
 * Everything else (a timeout, an expired session, a 5xx, a body that did not parse)
 * means we do not KNOW what paying would take. Treating that as "no quote exists"
 * is how a network blip becomes a charge the payer never reviewed: `approved` is
 * omitted, so the server has nothing to hold the amount to and takes whatever the
 * invoice and the credit balance happen to say. So it closes payment instead.
 *
 * A 404 is neither failure: the invoice is gone, or is not this company's. The route
 * answers both with the binding's empty 404, so that a caller cannot tell them apart,
 * and every retry gets the same answer. So it closes the press as an invoice that has
 * moved on, with no retry to offer.
 *
 * THE STATUS IS THE SIGNAL for THAT split — read vs refused — because it is the only
 * one this route publishes: every refusal it gives is one 422 in `payInvoicePreview`
 * or the binding's 404, and nothing else on a route with no request body to validate
 * answers either.
 *
 * BUT THE 422 IS OVERLOADED, and one of the answers inside it is not payable. The
 * route shares that status across three refusals (see `payInvoicePreview`): the
 * renewal whose total the charge recalculates, the invoice `assertPayable` rejects
 * — paid or canceled since the list was read — and the invoice `assertQuotable`
 * rejects because a recorded attempt is already resolving against it, which is what
 * an OPEN row that has turned PENDING underneath us looks like. Only the renewal is
 * still payable without a quote. For the other two, offering the press invites a
 * request the pay endpoint must refuse.
 *
 * THE SERVER IS THE ONLY ONE THAT KNOWS WHICH of the three it gave, so the split
 * reads its `reason` and nothing else ({@see UNQUOTABLE_RENEWAL}). The row's own
 * `type` cannot answer it: a renewal that was settled or canceled between the
 * list load and this request is refused for THAT, and comes back as a 422 off a
 * row still typed `renewal` — so a type test re-opens the unquoted press on the
 * invoice the pay endpoint must turn down.
 */
async function loadPreview() {
    if (!single.value || !companyId.value) return;
    isLoadingPreview.value = true;
    previewRefusal.value = null;
    previewFailure.value = null;
    notPayable.value = null;
    try {
        preview.value = await businessService.getInvoicePayPreview(companyId.value, invoices.value[0].id);
    } catch (error: unknown) {
        preview.value = null;
        if (error instanceof ApiError && error.code === 422) {
            // Authored by our API for this invoice, so either way it is the sentence to
            // show; the REASON is what says whether a press can still work — and its
            // absence says nothing, so it takes the branch that charges nothing.
            if (error.reason === UNQUOTABLE_RENEWAL) {
                previewRefusal.value = error.message || 'This invoice cannot be quoted before it is paid.';
            } else {
                notPayable.value = error.message || NOT_PAYABLE_SENTENCE;
            }
        } else if (error instanceof ApiError && error.code === 404) {
            // An empty body: the API wrote no sentence, and ofetch's names the whole URL.
            notPayable.value = NOT_PAYABLE_SENTENCE;
        } else {
            // Authored by machinery — ofetch names the whole URL — so it is not shown.
            previewFailure.value = memberFacingMessage(error, 'What paying this invoice takes could not be read just now.');
        }
    } finally {
        isLoadingPreview.value = false;
    }
}

/**
 * Only a figure the payer actually SAW may be pinned. The multi-invoice
 * confirmation shows each invoice's own total and never quotes the card amount,
 * so it promises nothing and pins nothing — which the API accepts as exactly
 * that.
 */
const approvedFor = (invoice: PayableInvoice): ApprovedCharge | undefined => {
    const quote = preview.value;
    if (!single.value || !quote || quote.invoice_id !== invoice.id) return undefined;

    return {
        amount_due_cents: quote.amount_due_cents,
        credit_applied_cents: quote.credit_applied_cents,
        card_brand: quote.card_brand,
        card_last_four: quote.card_last_four,
    };
};

/**
 * False from the moment this dialog is torn down.
 *
 * A Pay all run is a loop of awaits, and unmounting the component does not cancel it.
 * The financial page closes every dialog it opened when it unmounts (`closeOpenDialogs`),
 * a company switch included, so without this the loop would go on charging the remaining
 * invoices from behind a closed modal, and the payer would never see the outcome of a
 * single one of them.
 */
let live = true;
/** Ends a bank step's watch on a charge still processing, once nobody is left to tell. */
const teardown = new AbortController();
onBeforeUnmount(() => {
    live = false;
    teardown.abort();
    data.value?.onWriting?.(false);
});

// The opener hears the whole run, from the first charge to the outcome (`onWriting`), every
// bank step included: its unmount would close this dialog mid-run, past the disabled close,
// and the payer would never see what the run settled. A charge already sent lands either way,
// so the teardown above releases the opener too.
watch(isPaying, (paying) => data.value?.onWriting?.(paying));

/** The pay refusal that sends a single invoice back to review rather than into the outcome (see `pay`). */
const QUOTE_STALE = 'quote_stale';

/**
 * What a failed charge says where the API has not vouched for its own sentence and the invoice
 * is not known to be gone: true of every such failure, including a timeout that may yet have
 * taken the money.
 */
const UNCONFIRMED_PAYMENT_SENTENCE = 'This payment could not be confirmed — check this invoice before paying it again.';

/**
 * The bucket a failed charge lands in: its group in the one map every payment screen reads
 * (`paymentRefusalGroup`), told the invoice's own status when the route reported one. A charge
 * that may still take money is not confirmed, and another payment holding the payer's lock left
 * this invoice still open: nothing was charged on it.
 */
function unpaidBucket(error: unknown): UnpaidBucket {
    const group = paymentRefusalGroup(error, error instanceof ApiError ? error.envelopeDetails.invoice_status : undefined);

    if (group === 'inFlight') return 'unconfirmed';
    if (group === 'busy') return 'stillOpen';

    return group;
}

/**
 * What a bank step that took nothing says. The pay route held the invoice PENDING with the attempt
 * recorded when it asked for the step, and this page pays OPEN invoices only while the preview
 * refuses one with an attempt in flight, so it cannot be paid from here until app-api clears it.
 */
const BANK_STEP_UNFINISHED_SENTENCE =
    "Your bank's verification wasn't completed, so nothing was charged. This invoice can't be paid from here until that unfinished payment clears.";

/**
 * The refusals of the second press that mean the payment the bank took is still being recorded,
 * not that anything went wrong: app-api's two reasons for a payment in flight, which it asks a
 * client to show as a payment still being confirmed, never as a failure, and another request
 * holding the payer's lock, the Stripe webhook recording this same payment among them.
 */
const STILL_RECORDING: ReadonlySet<string> = new Set(['payment_in_progress', 'payment_processing', 'payer_busy']);

/**
 * Asks app-api to record the payment the bank just confirmed, and answers whether it is recorded
 * or still being recorded. The pay route resolves the attempt it holds at Stripe before it would
 * price a charge, so asking again records the settled intent and charges nothing; the approved
 * figures go back with it, as the route expects of a replay. The Stripe webhook can record the
 * payment first, and the route then refuses an invoice that reads `paid`, which is the same
 * answer. Any other answer leaves the payment unrecorded, which the shared flow reports with its
 * reference.
 */
async function recordSettled(company: number, invoice: PayableInvoice): Promise<'recorded' | 'recording'> {
    try {
        const outcome = await businessService.payInvoice(company, invoice.id, approvedFor(invoice));
        if (outcome.kind === 'paid') return 'recorded';
    } catch (error: unknown) {
        if (error instanceof ApiError && error.envelopeDetails.invoice_status === 'paid') return 'recorded';
        if (error instanceof ApiError && STILL_RECORDING.has(error.reason ?? '')) return 'recording';
        throw error;
    }

    throw new Error('The payment the bank confirmed is not recorded against this invoice');
}

/**
 * A charge app-api answered with a bank step (`ThreeDSecureV3Resource`): the card's bank must
 * authenticate it, nothing is taken yet, and the invoice is held PENDING. The payer completes
 * the step through the shared flow (`useThreeDSecure`), and the invoice is paid only once
 * app-api records the settled payment. A payment the bank took that is still being recorded,
 * one it took that app-api has not recorded, and a charge still processing are not confirmed.
 * A step that took nothing is on hold (`BANK_STEP_UNFINISHED_SENTENCE`).
 */
async function completeBankStep(company: number, invoice: PayableInvoice, action: ThreeDSecureChallenge): Promise<PayResult> {
    const result = await authenticate(action, () => recordSettled(company, invoice), teardown.signal);

    if (result.ok && result.confirmed === 'recorded') return { id: invoice.id, bucket: 'paid' };
    if (result.ok) return { id: invoice.id, bucket: 'unconfirmed', message: PROCESSING_MESSAGE };
    if (result.charged) return { id: invoice.id, bucket: 'unconfirmed', message: result.message };

    return { id: invoice.id, bucket: 'onHold', message: BANK_STEP_UNFINISHED_SENTENCE };
}

const pay = async () => {
    const company = companyId.value;
    // canCharge again, and here rather than only on the button: a disabled attribute
    // is one render away from being lost, and this is the seam the money crosses.
    if (isPaying.value || !company || !canCharge.value) return;
    phase.value = 'paying';
    staleNotice.value = null;

    const done: PayResult[] = [];

    for (const invoice of invoices.value) {
        // Between invoices, not inside one: a charge already sent has to be waited for and
        // recorded, or the money moves with nothing on this side knowing. What stops is the
        // NEXT one, which is the only decision still open. So a bank step runs to its end too,
        // and the next invoice's charge waits for it.
        if (!live) return;

        try {
            const outcome = await businessService.payInvoice(company, invoice.id, approvedFor(invoice));
            done.push(outcome.kind === 'paid'
                ? { id: invoice.id, bucket: 'paid' }
                : await completeBankStep(company, invoice, outcome.action));
        } catch (error: unknown) {
            // The figures the payer approved no longer hold — another invoice spent
            // the credit, or the primary card was swapped. The card was not touched
            // and the invoice is still payable, so re-read the quote and let them
            // approve the new one rather than retrying blind against a number
            // nobody saw.
            if (error instanceof ApiError && error.reason === QUOTE_STALE && single.value) {
                await loadPreview();
                staleNotice.value = error.message;
                phase.value = 'review';
                return;
            }

            // Machinery prose does not reach a payer, here as on the preview: a charge
            // that never left the browser carries ofetch's own sentence, which names the
            // method and the whole API URL, and this list is the customer's copy of what
            // happened. An envelope `reason` is the proof the API wrote the sentence for
            // a person, so it is what unlocks it. Without one the modal speaks for itself:
            // an invoice that is gone can no longer be paid, and anything else gets the one
            // thing true of every failure here.
            const bucket = unpaidBucket(error);
            done.push({
                id: invoice.id,
                bucket,
                message: memberFacingMessage(error, bucket === 'movedOn' ? NOT_PAYABLE_SENTENCE : UNCONFIRMED_PAYMENT_SENTENCE),
            });
        }
    }

    if (!live) return;

    results.value = done;
    phase.value = 'done';
};

// Both counts go back: the opener announces only `paid`, but it refreshes on
// either, because a refused charge can still have moved its invoice out of OPEN —
// to PENDING while it settles, or to FAILED. A dismissal reports zero of each,
// which is how it stays a dismissal.
const close = () => {
    dialogRef.value?.close({
        paid: tally.value.paid,
        failed: failures.value.length,
        // Nothing was charged, and the list behind this modal is still wrong: the row
        // that opened it has moved on, and it is still drawing a Pay button. Refreshing
        // is the opener's to do — this modal cannot re-read one invoice, and asking the
        // preview again returns the same refusal for as long as the invoice stays where
        // it now is.
        refresh: notPayable.value !== null,
    });
};

onMounted(loadPreview);
</script>

<style lang="scss" scoped>
.close-btn {
    @apply flex h-8 w-8 items-center justify-center rounded-full text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-700;
}

.pay-body {
    @apply w-full;
}

.pay-lines {
    @apply flex w-full flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3;
}

.pay-line {
    @apply flex items-start justify-between gap-3 text-sm leading-5;

    dt {
        @apply text-zinc-600;
    }

    dd {
        @apply flex flex-col items-end gap-0.5 text-right font-medium text-zinc-950;
    }

    &--total {
        @apply border-t border-zinc-200 pt-2 text-base;
    }
}

.pay-line__sub {
    @apply text-xs font-normal text-zinc-600;
}

.pay-card {
    @apply flex items-center gap-2;

    :deep(svg) {
        @apply h-5 w-auto;
    }
}

.pay-note {
    @apply mb-0 mt-3 text-xs leading-5 text-zinc-600;
}

.pay-warning {
    @apply mb-0 mt-3 text-sm font-semibold text-blue-700;
}

.pay-outcome {
    @apply mb-0 text-sm leading-5 text-zinc-950;
}

.pay-failures {
    @apply mt-3 flex w-full flex-col gap-2 rounded-md border border-zinc-200 bg-zinc-50 p-3;

    li {
        @apply flex flex-col gap-0.5 text-xs leading-5 text-zinc-600;
    }

    strong {
        @apply text-sm text-zinc-950;
    }
}
</style>
